/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU128Decoder,
  getU128Encoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';

export const POSITION_DISCRIMINATOR = new Uint8Array([
  170, 188, 143, 228, 122, 64, 247, 208,
]);

export function getPositionDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(POSITION_DISCRIMINATOR);
}

export type Position = {
  discriminator: ReadonlyUint8Array;
  pool: Address;
  owner: Address;
  positionId: bigint;
  shares: bigint;
  feeGrowthLast0Q64: bigint;
  feeGrowthLast1Q64: bigint;
  feeOwed0: bigint;
  feeOwed1: bigint;
  version: number;
  reserved: ReadonlyUint8Array;
};

export type PositionArgs = {
  pool: Address;
  owner: Address;
  positionId: number | bigint;
  shares: number | bigint;
  feeGrowthLast0Q64: number | bigint;
  feeGrowthLast1Q64: number | bigint;
  feeOwed0: number | bigint;
  feeOwed1: number | bigint;
  version: number;
  reserved: ReadonlyUint8Array;
};

/** Gets the encoder for {@link PositionArgs} account data. */
export function getPositionEncoder(): FixedSizeEncoder<PositionArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['pool', getAddressEncoder()],
      ['owner', getAddressEncoder()],
      ['positionId', getU64Encoder()],
      ['shares', getU128Encoder()],
      ['feeGrowthLast0Q64', getU128Encoder()],
      ['feeGrowthLast1Q64', getU128Encoder()],
      ['feeOwed0', getU64Encoder()],
      ['feeOwed1', getU64Encoder()],
      ['version', getU8Encoder()],
      ['reserved', fixEncoderSize(getBytesEncoder(), 7)],
    ]),
    (value) => ({ ...value, discriminator: POSITION_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link Position} account data. */
export function getPositionDecoder(): FixedSizeDecoder<Position> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['pool', getAddressDecoder()],
    ['owner', getAddressDecoder()],
    ['positionId', getU64Decoder()],
    ['shares', getU128Decoder()],
    ['feeGrowthLast0Q64', getU128Decoder()],
    ['feeGrowthLast1Q64', getU128Decoder()],
    ['feeOwed0', getU64Decoder()],
    ['feeOwed1', getU64Decoder()],
    ['version', getU8Decoder()],
    ['reserved', fixDecoderSize(getBytesDecoder(), 7)],
  ]);
}

/** Gets the codec for {@link Position} account data. */
export function getPositionCodec(): FixedSizeCodec<PositionArgs, Position> {
  return combineCodec(getPositionEncoder(), getPositionDecoder());
}

export function decodePosition<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<Position, TAddress>;
export function decodePosition<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<Position, TAddress>;
export function decodePosition<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<Position, TAddress> | MaybeAccount<Position, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getPositionDecoder(),
  );
}

export async function fetchPosition<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<Position, TAddress>> {
  const maybeAccount = await fetchMaybePosition(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybePosition<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<Position, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodePosition(maybeAccount);
}

export async function fetchAllPosition(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<Position>[]> {
  const maybeAccounts = await fetchAllMaybePosition(rpc, addresses, config);
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybePosition(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<Position>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodePosition(maybeAccount));
}

export function getPositionSize(): number {
  return 152;
}
